import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import UserList from "./UserList";
import UserRightPanel from "./UserRightPanel";
import UserDetailsDrawer from "./UserDetailsDrawer";
import { defaultUsers } from "./test-data";

const UserDirectory = ({ users = defaultUsers }) => {
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [bottomOpen, setBottomOpen] = useState(false);
  const [availableHeight, setAvailableHeight] = useState(680);
  const containerRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const selectedUser =
    users.find((u) => String(u.id) === String(selectedUserId)) || null;

  // Calculate available height by measuring the actual container
  useEffect(() => {
    const calculateHeight = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const containerTop = containerRect.top;
        const availableHeight = Math.max(
          400,
          viewportHeight - containerTop - 24
        ); // 24px for padding
        setAvailableHeight(availableHeight);
      }
    };

    // Calculate on mount and resize
    calculateHeight();
    window.addEventListener("resize", calculateHeight);

    return () => window.removeEventListener("resize", calculateHeight);
  }, []);

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          gap: 2,
          height: `${availableHeight}px`,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Left Panel: Users List */}
        <Box
          sx={{
            flex: { xs: "none", md: "0 0 400px" },
            width: { xs: "100%", md: "auto" },
            height: { xs: "50%", md: "100%" },
            minHeight: { xs: "300px", md: "auto" },
          }}
        >
          <UserList
            users={users}
            selectedUserId={selectedUserId}
            height="100%"
            onSelect={(id) => {
              setSelectedUserId(id);
              if (isMobile) setBottomOpen(true);
            }}
          />
        </Box>

        {/* Right Side: User Details */}
        {!isMobile && (
          <Box sx={{ flex: 1, height: "100%" }}>
            {selectedUserId ? (
              <UserRightPanel
                activeTab={activeTab}
                onChangeTab={(v) => setActiveTab(v)}
                selectedUser={selectedUser}
                allUsers={users}
              />
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px dashed #e0e0e0",
                  borderRadius: 1,
                  color: "#888",
                }}
              >
                <Typography sx={{ fontSize: { xs: "1.1rem", md: "1.2rem" } }}>
                  Select a user to view details
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Mobile: Bottom slide-up drawer when a user is selected */}
      {isMobile && selectedUser && (
        <UserDetailsDrawer
          open={bottomOpen}
          onClose={() => setBottomOpen(false)}
          activeTab={activeTab}
          onChangeTab={(v) => setActiveTab(v)}
          selectedUser={selectedUser}
          allUsers={users}
        />
      )}
    </>
  );
};

export default UserDirectory;
